import { useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import {
  getOrgProfileByName,
  getOrgProfilesByParentId,
} from "@/actions/org-profiles";
import type { WorkTreeModel } from "@/models/WorkTreeModel";
import type { WorkStatus } from "@/types/table-tree";
import { isValidOrgId } from "@/lib/utils";

export const useWorkStatuses = () => {
  const { orgId } = useParams();

  const { data: workStatuses, isLoading } = useQuery<WorkStatus[]>({
    queryKey: ["workStatuses", orgId],
    queryFn: async () => {
      const id = orgId as string;
      const workStatusKey = await getOrgProfileByName(id, "Work Status");
      if (workStatusKey && workStatusKey[0]?.id) {
        const workStatusProfiles = await getOrgProfilesByParentId(
          id,
          workStatusKey[0].id,
        );
        return workStatusProfiles || [];
      }
      return [];
    },
    enabled: isValidOrgId(orgId as string | undefined),
  });

  // Cache for complete status ID to avoid repeated searches
  const completeStatusIdRef = useRef<string | null>(null);

  // Memoize the complete status ID to avoid repeated searches
  const completeStatusId = useMemo(() => {
    if (completeStatusIdRef.current) {
      return completeStatusIdRef.current;
    }

    const found = workStatuses?.find(
      (status) =>
        status.name?.toLowerCase() === "complete" ||
        status.name?.toLowerCase() === "completed" ||
        status.name?.toLowerCase() === "done",
    )?.id;

    completeStatusIdRef.current = found || null;
    return found || null;
  }, [workStatuses]);

  const isTaskComplete = useCallback(
    (workItem: WorkTreeModel): boolean => {
      return completeStatusId != null && completeStatusId === workItem.statusId;
    },
    [completeStatusId],
  );
  //this returns only 3 items or items defined in the DB. This does not return "Incomplete" option.
  return { workStatuses, isLoading, completeStatusId, isTaskComplete };
};
